import { Link, useLocation } from 'react-router-dom'
import { ChevronRight, Home } from 'lucide-react'
import { useTranslation } from 'react-i18next'

// URL paths that are namespaces, not real routes — rendered as plain
// text in the breadcrumb instead of links (clicking them would 404).
// Derived by cross-referencing every path in routes.tsx against
// every ancestor path a breadcrumb would try to link. Keep in sync when
// adding or removing top-level URL namespaces. Indexed by full path so
// segments with context-dependent validity (e.g. `roles` is valid under
// `/admin/roles` but not under `/admin/access-matrix/roles`) stay
// correct.
//
// Known limitation: dynamic :id ancestors that have no matching
// `/path/:id` index route (e.g. `/admin/facilities/:id` when only
// `.../:id/edit` is registered) are NOT handled here — clicking such a
// breadcrumb entry will 404. Fixing that cleanly needs a central route
// registry the breadcrumb can run matchPath against. Tracked as a
// separate breadcrumb refactor.
const nonNavigablePaths = new Set<string>([
  // Top-level URL namespaces (no parent page).
  '/admin',
  '/analytics',
  '/billing',
  '/encounters',
  '/medications',
  '/notes',
  '/orders',
  '/patients',
  '/results',
  // Second-level namespaces that only exist as URL prefixes.
  '/admin/licensing',
  '/admin/integrations/fhir-endpoints',
  '/admin/integrations/smart-apps',
  '/admin/taxonomy/clinical-services',
  '/admin/taxonomy/service-lines',
  '/admin/access-matrix/categories',
  '/admin/access-matrix/roles',
  '/admin/departments',
])

// URL-segment → i18n key mapping for the breadcrumb. Resolved against
// the `nav` namespace at render time, so labels track the active
// language. Two categories:
//
//   1. Sidebar-aligned segments reuse the same flat `nav.<name>` keys
//      the sidebar uses (Task P1-6a is the canonical owner of those).
//   2. Breadcrumb-only segments — sub-paths the sidebar doesn't
//      surface as top-level items (e.g. `search` under `/patients/search`,
//      `register`, `discharge`, etc.) — live under `nav.segments.<name>`.
//
// Anything missing from this map falls through to `formatSegment`,
// which title-cases the raw URL segment.
const segmentKeyMap: Record<string, string> = {
  // Sidebar-aligned (flat `nav.*`)
  dashboard: 'dashboard',
  patients: 'patients',
  my: 'myPatients',
  encounters: 'encounters',
  orders: 'orders',
  results: 'results',
  medications: 'medications',
  formulary: 'formulary',
  notes: 'notes',
  appointments: 'appointments',
  waitlist: 'waitlist',
  charges: 'charges',
  claims: 'claims',
  payments: 'payments',
  accounts: 'accounts',
  statements: 'statements',
  reports: 'reports',
  users: 'users',
  roles: 'roles',
  facilities: 'facilities',
  providers: 'providers',
  integrations: 'integrations',
  system: 'system',
  'file-key': 'fileKey',
  // Sidebar-aligned (group headers in `nav.groups.*`)
  billing: 'groups.billing',
  analytics: 'groups.analytics',
  licensing: 'groups.licensing',
  // Breadcrumb-only segments (`nav.segments.*`)
  search: 'segments.search',
  register: 'segments.register',
  demographics: 'segments.demographics',
  census: 'segments.census',
  discharge: 'segments.discharge',
  catalog: 'segments.catalog',
  sets: 'segments.sets',
  pending: 'segments.pending',
  critical: 'segments.critical',
  reconciliation: 'segments.reconciliation',
  worklist: 'segments.worklist',
  templates: 'segments.templates',
  'smart-phrases': 'segments.smartPhrases',
  schedule: 'segments.schedule',
  quality: 'segments.quality',
  admin: 'segments.administration',
  audit: 'segments.auditLog',
  allergies: 'segments.allergies',
  problems: 'segments.problems',
  immunizations: 'segments.immunizations',
  insurance: 'segments.insurance',
}

function fallbackLabel(segment: string): string {
  // If it looks like an ID (numeric or UUID-like), show it as-is
  if (/^\d+$/.test(segment) || /^[a-f0-9-]{8,}$/.test(segment)) return `#${segment}`
  // Capitalize and replace hyphens
  return segment
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

export default function Breadcrumb() {
  const location = useLocation()
  const segments = location.pathname.split('/').filter(Boolean)
  const { t } = useTranslation('nav')

  const formatSegment = (segment: string): string => {
    const key = segmentKeyMap[segment]
    return key ? t(key) : fallbackLabel(segment)
  }

  if (segments.length === 0) return null

  return (
    <nav aria-label={t('breadcrumbAriaLabel')} className="flex items-center gap-1.5 text-sm text-muted-foreground">
      <Link
        to="/dashboard"
        aria-label={t('homeAriaLabel')}
        className="flex items-center gap-1 hover:text-foreground transition-colors"
      >
        <Home className="h-3.5 w-3.5" />
      </Link>
      {segments.map((segment, index) => {
        const path = '/' + segments.slice(0, index + 1).join('/')
        const isLast = index === segments.length - 1
        const isNamespace = nonNavigablePaths.has(path)

        return (
          <span key={path} className="flex items-center gap-1.5">
            <ChevronRight className="h-3.5 w-3.5" />
            {isLast || isNamespace ? (
              <span className={isLast ? 'font-medium text-foreground' : ''}>
                {formatSegment(segment)}
              </span>
            ) : (
              <Link to={path} className="hover:text-foreground transition-colors">
                {formatSegment(segment)}
              </Link>
            )}
          </span>
        )
      })}
    </nav>
  )
}
